<template>
  <div class="full-page-height">
    <SavingDialog :opened.sync="savingDialogOpened" />

    <!-- Partial: Alerts -->
    <sc-alert
      v-if="taxReturn.status === 'action_required'"
      type="info"
      class="instruction"
    >
      <template v-if="!taxReturn.tax_details_extraction_completed">
        <span>
          Please wait while we're extracting tax details from the PDF.
        </span>

        <sc-button-group slot="button-group">
          <sc-button type="ghost" is-disabled icon-right="loading" />
        </sc-button-group>
      </template>

      <template v-else>
        <span>
          <b>Instruction:</b> Please review and ensure all tax information is
          correct before proceeding.
        </span>

        <sc-button-group slot="button-group">
          <sc-button
            type="ghost"
            @click="finishReviewing()"
            :is-disabled="saving"
            icon-right="arrow-right"
            data-test-id="delivery-next-tax-summary"
          >
            Finished Reviewing
          </sc-button>
        </sc-button-group>
      </template>
    </sc-alert>

    <sc-alert v-else type="info" class="instruction">
      <span>
        Note: This page is READONLY because the tax return has been delivered.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="next()"
          :is-disabled="saving"
          icon-left="arrow-right"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-flex>
      <!-- Partial: Left Sidebar (Bookmarks) -->
      <sc-flex class="left-sidebar" is-column>
        <sc-h2 class="px-2 py-2">Index</sc-h2>
        <div v-for="(returnData, index) in allReturnsData" :key="index">
          <h4 class="mx-2">
            {{
              index === 0
                ? 'Main Package'
                : allReturns[index].custom_return_name
            }}
            <span v-if="index > 0" class="pl-1 grey--text">
              (Paper-filed return)
            </span>
          </h4>

          <v-treeview
            :open.sync="returnData.openItems"
            :items="returnData.bookmarks"
            :active="activeItems"
            class="bookmark-tree"
          >
            <template v-slot:label="{ item }">
              <div class="bookmark-label" @click="handleItemClick(item)">
                <span>{{ item.name }}</span>
              </div>
            </template>
          </v-treeview>

          <v-divider class="py-2" />
        </div>
      </sc-flex>

      <!-- Partial: PDF Viewer -->
      <div class="pdf-container">
        <nav class="toolbar">
          <sc-flex
            class="toolbar__pageNavigation"
            :is-centered="true"
            :gap="8"
            v-if="currentReturnData"
          >
            <sc-button-group>
              <sc-button
                type="secondary"
                @click="pdfPageIndex -= pdfPageIndex > 1 ? 1 : 0"
                :is-disabled="pdfPageIndex === 1"
                :is-icon-only="true"
                icon-left="arrow-left"
                size="small"
              ></sc-button>
            </sc-button-group>
            <sc-flex :is-y-centered="true" :gap="8">
              <sc-small>Page:</sc-small>
              <input
                :min="1"
                :max="currentReturnData.pageCount"
                type="number"
                :value="pdfPageIndex"
                @change="
                  pdfPageIndex = Math.min(
                    Math.max(1, $event.target.value),
                    currentReturnData.pageCount,
                  )
                "
              />
              <sc-small>/ {{ currentReturnData.pageCount }}</sc-small>
            </sc-flex>
            <sc-button-group>
              <sc-button
                type="secondary"
                @click="
                  pdfPageIndex +=
                    pdfPageIndex < currentReturnData.pageCount ? 1 : 0
                "
                :is-disabled="pdfPageIndex === currentReturnData.pageCount"
                :is-icon-only="true"
                icon-right="arrow-right"
                size="small"
              >
              </sc-button>
            </sc-button-group>
          </sc-flex>
        </nav>

        <v-layout class="page-container">
          <v-layout class="pdf-page-container">
            <div class="pdf-wrapper">
              <pdf
                v-if="currentReturnData && currentReturnData.pdfData"
                :page="pdfPageIndex"
                :src="currentReturnData.pdfData"
                class="pdf ph-no-capture"
              />

              <!-- Bounding box overlay -->
              <div
                v-if="currentBoundingBox"
                class="bounding-box-overlay"
                :style="{
                  left: currentBoundingBox.left,
                  top: currentBoundingBox.top,
                  width: currentBoundingBox.width,
                  height: currentBoundingBox.height,
                }"
              ></div>
            </div>
          </v-layout>

          <!-- Partial: Right Sidebar (Tax return information) -->
          <sc-flex class="right-sidebar" is-column>
            <v-tooltip bottom z-index="1000">
              <v-chip class="mx-0 mb-2 beta-chip" slot="activator" small>
                Beta
              </v-chip>

              <span
                >This feature is in Beta. There may be inaccuracies and errors.
                Please review the extracted information.
              </span>
            </v-tooltip>

            <div
              v-if="shouldHighlightErrors && !areTaxDetailsReviewed()"
              class="mb-2"
            >
              <v-alert :value="true" color="error" icon="error">
                <v-layout column>
                  <span>
                    Please review all the numbers by clicking on the input
                    fields highlighted in red.
                  </span>
                </v-layout>
              </v-alert>
            </div>

            <sc-alert
              v-if="
                taxReturn.status === 'action_required' &&
                  !taxReturn.tax_details_extraction_completed
              "
              type="note"
            >
              <span>
                Please wait while we're extracting tax details from the PDF.
              </span>
            </sc-alert>

            <div v-else v-for="(returnData, taxReturnIndex) in allReturnsData">
              <h4 class="mb-2">
                {{
                  taxReturnIndex === 0
                    ? 'Main Package'
                    : allReturns[taxReturnIndex].custom_return_name
                }}
                <span v-if="taxReturnIndex > 0" class="pl-1 grey--text">
                  (Paper-filed return)
                </span>
              </h4>

              <div
                v-if="isExtractingDetailForReturn(taxReturnIndex)"
                class="mb-4"
              >
                <v-alert
                  :value="true"
                  color="info"
                  outline
                  class="alert-extracting"
                  :class="{
                    errored: shouldHighlightErrors,
                  }"
                >
                  <v-layout column>
                    <span>
                      We're still extracting tax summary from this return (if
                      you want to enter the summary manually, press the Cancel
                      button to stop).
                    </span>

                    <v-progress-linear
                      :indeterminate="true"
                    ></v-progress-linear>

                    <v-btn
                      class="mx-0 mt-2 text-none"
                      small
                      color="primary"
                      @click="cancelExtraction(taxReturnIndex)"
                    >
                      <span class="white--text">
                        Cancel extraction
                      </span>
                    </v-btn>
                  </v-layout>
                </v-alert>
              </div>

              <div v-else class="tax-details-container">
                <div
                  v-for="(detail, taxDetailIndex) in returnData.taxDetails"
                  :key="taxDetailIndex"
                  class="tax-detail-card"
                >
                  <div class="card-title-container">
                    <template
                      v-if="
                        editingTitles.find(
                          item =>
                            item.taxReturnIndex === taxReturnIndex &&
                            item.taxDetailIndex === taxDetailIndex,
                        )
                      "
                    >
                      <input
                        type="text"
                        v-model="detail.title"
                        @blur="stopEditingTitle(taxReturnIndex, taxDetailIndex)"
                        @keyup.enter="
                          stopEditingTitle(taxReturnIndex, taxDetailIndex)
                        "
                        class="editable-title-input"
                      />
                    </template>
                    <template v-else>
                      <sc-h5>{{ detail.title }}</sc-h5>
                    </template>

                    <div v-if="editable" class="title-actions">
                      <v-btn
                        outline
                        fab
                        small
                        class="icon-btn"
                        color="primary"
                        @click="
                          editingTitles.find(
                            item =>
                              item.taxReturnIndex === taxReturnIndex &&
                              item.taxDetailIndex === taxDetailIndex,
                          )
                            ? stopEditingTitle(taxReturnIndex, taxDetailIndex)
                            : startEditingTitle(taxReturnIndex, taxDetailIndex)
                        "
                      >
                        <v-icon class="material-icons-outlined">
                          {{
                            editingTitles.find(
                              item =>
                                item.taxReturnIndex === taxReturnIndex &&
                                item.taxDetailIndex === taxDetailIndex,
                            )
                              ? 'check'
                              : 'edit'
                          }}
                        </v-icon>
                      </v-btn>

                      <v-btn
                        outline
                        fab
                        small
                        @click="deleteTaxDetail(taxReturnIndex, taxDetailIndex)"
                        class="icon-btn"
                        color="error"
                        v-if="
                          !editingTitles.find(
                            item =>
                              item.taxReturnIndex === taxReturnIndex &&
                              item.taxDetailIndex === taxDetailIndex,
                          )
                        "
                      >
                        <v-icon class="material-icons-outlined">
                          delete
                        </v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <v-divider class="my-2"></v-divider>

                  <div
                    v-for="(row, rowIndex) in detail.rows"
                    :key="rowIndex"
                    class="detail-row-sidebar form-control"
                    v-if="row.label in rowOptions"
                    :class="rowOptions[row.label].cssClass"
                  >
                    <v-layout class="align-center">
                      <div class="row-label">
                        <span>{{ row.label }}</span>
                      </div>

                      <div class="row-value">
                        <CurrencyInput
                          v-if="rowOptions[row.label].editable"
                          v-model="
                            shouldUseValueFromFederalTaxes(detail.title, row)
                              ? getValueFromFederalTaxes(row.label)
                              : row.value
                          "
                          :options="{ currency: 'USD' }"
                          :disabled="
                            !editable ||
                              shouldUseValueFromFederalTaxes(detail.title, row)
                          "
                          class="sidebar-currency-input"
                          :class="{
                            'focused-input': isFocused(
                              taxReturnIndex,
                              taxDetailIndex,
                              rowIndex,
                            ),
                            errored:
                              shouldHighlightErrors && !isRowReviewed(row),
                          }"
                          @focus="
                            handleCurrencyInputFocus(
                              row,
                              taxReturnIndex,
                              taxDetailIndex,
                              rowIndex,
                            )
                          "
                          @blur="handleCurrencyInputBlur"
                        />

                        <span v-else>
                          {{ getTrueValue(detail.title, row) }}
                        </span>
                      </div>
                    </v-layout>

                    <div
                      v-if="canUseValueFromFederalTaxes(detail.title, row)"
                      class="d-flex align-center justify-between"
                    >
                      <v-switch
                        :disabled="!editable"
                        v-model="row.use_federal_taxes_value"
                        class="mt-0 mb-1 pr-0"
                        hide-details
                      >
                        <template v-slot:label>
                          <v-layout align-center justify-center>
                            <span style="font-size: 12px;"
                              >Use value from Federal Taxes</span
                            >

                            <v-tooltip bottom z-index="1000">
                              <v-icon
                                slot="activator"
                                class="material-icons-outlined grey--text tooltip-icon ml-2"
                                small
                              >
                                info
                              </v-icon>
                              <span
                                >You can use this to override the value if your
                                state form uses a different basis from the
                                federal form.</span
                              >
                            </v-tooltip>
                          </v-layout>
                        </template>
                      </v-switch>
                    </div>
                  </div>
                </div>

                <div v-if="editable">
                  <v-btn
                    color="primary"
                    small
                    block
                    :disabled="saving"
                    @click="addTaxDetail(taxReturnIndex)"
                    class="text-none my-0"
                  >
                    <v-icon small class="mr-1">add</v-icon>
                    Add Tax Summary
                  </v-btn>
                </div>

                <v-divider class="my-3"></v-divider>
              </div>
            </div>
          </sc-flex>
        </v-layout>
      </div>
    </sc-flex>
  </div>
</template>

<script>
import API from '@/api';
import pdf from 'vue-pdf';
import { PDFDocument } from 'pdf-lib';
import CurrencyInput from '@/components/returns/CurrencyInput';
import SavingDialog from '@/components/returns/SavingDialog';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import TaxDetailMixin from '@/mixins/TaxDetailMixin';
import { mapGetters } from 'vuex';

export default {
  mixins: [TaxDetailMixin],

  components: {
    pdf,
    CurrencyInput,
    SavingDialog,
  },

  props: {
    taxReturn: {
      type: Object,
      required: true,
    },

    currentCompany: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      saving: false,
      savingDialogOpened: false,

      editingTitles: [],

      pollingIntervals: {},
      POLLING_INTERVAL: 2000,

      // Used to keep track which input is in focus
      focusing: null, // null | { taxDetailIndex: number, rowIndex: number }

      // Bounding box overlay data
      currentBoundingBox: null,

      // Error validation
      shouldHighlightErrors: false,

      isLoadingAllReturns: false,
      pdfPageIndex: null,

      // 0 is the main tax return
      // 1 >= the paper-file returns
      currentReturnIndex: 0,

      allReturnsData: [],

      shouldHighlightErrors: false,
    };
  },

  methods: {
    async downloadAllReturnData() {
      await Promise.all(
        this.allReturns.map(async (taxReturn, index) => {
          // Initialize with empty data
          this.$set(this.allReturnsData, index, {
            pdfDoc: null,
            pdfData: null,
            pageCount: 0,
            bookmarks: [],
            openItems: [], // bookmarks that are expanded
            taxDetails: taxReturn.tax_details || [],
            taxDetailsExtractionStatus: taxReturn.tax_details_extraction_status,
          });

          await this.downloadReturnData(taxReturn, index);
        }),
      );
    },

    // taxReturn here could be the main return or the paper-filed return
    async downloadReturnData(taxReturn, taxReturnIndex) {
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: taxReturn.original_file
          ? taxReturn.original_file
          : taxReturn.document_path,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });

      const bookmarks = await this.extractBookmarksFromPdf(
        pdfSrc,
        taxReturnIndex,
      );

      this.$set(this.allReturnsData, taxReturnIndex, {
        ...this.allReturnsData[taxReturnIndex],
        pdfDoc,
        pdfData,
        pageCount: pdfDoc.getPageCount(),
        bookmarks: bookmarks,
      });
    },

    hasErrors() {
      if (this.isExtractingTaxDetails()) {
        return true;
      } else if (!this.areTaxDetailsReviewed()) {
        return true;
      } else {
        return false;
      }
    },

    isExtractingTaxDetails() {
      return (
        this.pollingIntervals &&
        Object.values(this.pollingIntervals).some(interval => interval !== null)
      );
    },

    isExtractingDetailForReturn(returnIndex) {
      const source =
        this.allReturns[returnIndex].original_file ||
        this.allReturns[returnIndex].document_path;

      if (source && this.pollingIntervals[source]) {
        return true;
      } else {
        return false;
      }
    },

    cancelExtraction(returnIndex) {
      if (returnIndex === 0) {
        // Main return cannot be cancelled
        return;
      }

      const source =
        this.allReturns[returnIndex].original_file ||
        this.allReturns[returnIndex].document_path;

      if (source && this.pollingIntervals[source]) {
        clearInterval(this.pollingIntervals[source]);
        this.pollingIntervals[source] = null;
        // Update the extraction status to 'cancelled'

        this.$set(this.allReturnsData, returnIndex, {
          ...this.allReturnsData[returnIndex],
          taxDetailsExtractionStatus: 'cancelled',
        });
      }
    },

    areTaxDetailsReviewed() {
      return this.allReturnsData.every(returnData =>
        returnData.taxDetails.every(taxDetail =>
          this.isTaxDetailReviewed(taxDetail),
        ),
      );
    },

    isTaxDetailReviewed(taxDetail) {
      return taxDetail.rows.every(row => this.isRowReviewed(row));
    },

    isRowReviewed(row) {
      if (row.use_federal_taxes_value) {
        return true; // If using federal taxes value, then we don't need to review it
      }

      return row.reviewed_at;
    },

    async save() {
      if (!this.editable) {
        return;
      }

      const paper_filed_returns =
        this.allReturns.length > 1
          ? this.allReturns.slice(1).map((returnData, index) => {
              return {
                ...returnData,
                tax_details: this.allReturnsData[index + 1].taxDetails,
                tax_details_extraction_status: this.allReturnsData[index + 1]
                  .taxDetailsExtractionStatus,
              };
            })
          : undefined;

      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.currentCompany.id,
        tax_details: this.allReturnsData[0].taxDetails,
        paper_filed_returns: paper_filed_returns,
      });

      return response.data;
    },

    async finishReviewing() {
      if (!this.editable) {
        return;
      }

      if (this.hasErrors()) {
        this.shouldHighlightErrors = true;
      } else {
        const newTaxReturn = await this.save();
        this.$emit('tax-details-updated', newTaxReturn);
        this.$emit('next');
      }
    },

    next() {
      this.$emit('next');
    },

    addTaxDetail(taxReturnIndex, title = 'Other State Taxes') {
      const newStateDetail = this.createNewTaxDetail(title);
      this.allReturnsData[taxReturnIndex].taxDetails.push(newStateDetail);
    },

    deleteTaxDetail(taxReturnIndex, taxDetailIndex) {
      if (window.confirm('Are you sure you want to delete this tax detail?')) {
        this.allReturnsData[taxReturnIndex].taxDetails.splice(
          taxDetailIndex,
          1,
        );
      }
    },

    startEditingTitle(taxReturnIndex, taxDetailIndex) {
      if (!this.editingTitles.includes(taxDetailIndex)) {
        this.editingTitles.push({
          taxReturnIndex,
          taxDetailIndex,
        });
      }
    },

    stopEditingTitle(taxReturnIndex, taxDetailIndex) {
      this.editingTitles = this.editingTitles.filter(
        item =>
          !(
            item.taxReturnIndex === taxReturnIndex &&
            item.taxDetailIndex === taxDetailIndex
          ),
      );
    },

    handleCurrencyInputFocus(row, taxReturnIndex, taxDetailIndex, rowIndex) {
      this.focusing = {
        taxReturnIndex,
        taxDetailIndex,
        rowIndex,
      };

      row.reviewed_at = new Date().getTime(); // Mark as reviewed when focused
      row.reviewed_by = this.currentUser.id;
    },

    handleCurrencyInputBlur() {
      this.focusing = null;
    },

    isFocused(taxReturnIndex, taxDetailIndex, rowIndex) {
      return (
        this.focusing &&
        this.focusing.taxReturnIndex === taxReturnIndex &&
        this.focusing.taxDetailIndex === taxDetailIndex &&
        this.focusing.rowIndex === rowIndex
      );
    },

    clearBoundingBox() {
      this.currentBoundingBox = null;
    },

    setBoundingBox(boundingBox) {
      if (
        boundingBox &&
        boundingBox.left !== undefined &&
        boundingBox.top !== undefined
      ) {
        const padding = 0.005;
        this.currentBoundingBox = {
          left: `${Math.max(0, boundingBox.left - padding) * 100}%`,
          top: `${Math.max(0, boundingBox.top - padding) * 100}%`,
          width: `${Math.min(
            1 - boundingBox.left + padding,
            boundingBox.width + padding * 2,
          ) * 100}%`,
          height: `${Math.min(
            1 - boundingBox.top + padding,
            boundingBox.height + padding * 2,
          ) * 100}%`,
        };
      } else {
        this.clearBoundingBox();
      }
    },

    scrollToBoundingBox(boundingBox) {
      // Get the PDF container element
      const pdfContainer = this.$el.querySelector('.pdf-page-container');
      const pdfElement = this.$el.querySelector('.pdf');

      if (!pdfContainer || !pdfElement) return;

      // Get the dimensions of the PDF element
      const pdfRect = pdfElement.getBoundingClientRect();
      const containerRect = pdfContainer.getBoundingClientRect();

      // Calculate the position of the bounding box relative to the PDF
      const boxTop = pdfRect.height * boundingBox.top;
      const boxHeight = pdfRect.height * boundingBox.height;
      const boxCenterY = boxTop + boxHeight / 2;

      // Calculate the scroll position to center the bounding box vertically
      const containerHeight = containerRect.height;
      const scrollTop = boxCenterY - containerHeight / 2;

      // Smooth scroll to the calculated position
      pdfContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    },

    async extractBookmarksFromPdf(pdfSrc, taxReturnIndex) {
      try {
        const pdflib = window.pdfjsLib;
        pdflib.GlobalWorkerOptions.workerSrc =
          'https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.mjs';

        const loadingTask = pdflib.getDocument({ url: pdfSrc });
        const pdf = await loadingTask.promise; // Store pdf.js document instance

        const outline = await pdf.getOutline();
        const numPages = pdf.numPages;

        let bookmarks = [];

        if (outline) {
          const processBookmarks = async bookmarks => {
            const processed = await Promise.all(
              bookmarks.map(async bookmark => {
                let pageIndex = null;

                // Extract page index correctly from dest[0]
                if (
                  bookmark.dest &&
                  bookmark.dest.length > 0 &&
                  bookmark.dest[0]
                ) {
                  try {
                    const resolvedPageIndex = await pdf.getPageIndex(
                      bookmark.dest[0],
                    );
                    pageIndex = resolvedPageIndex + 1; // Convert to 1-based index
                  } catch (error) {
                    console.warn(
                      `Failed to resolve page index for: ${bookmark.title}`,
                      error,
                    );
                  }
                }

                return {
                  id: uuid(),
                  taxReturnIndex,
                  pageIndex: pageIndex,
                  name: bookmark.title,
                  children: bookmark.items
                    ? await processBookmarks(bookmark.items)
                    : undefined,
                };
              }),
            );

            return processed;
          };

          bookmarks = await processBookmarks(outline);
        }

        // If the PDF file has no bookmarks
        // we just show the Page list instead
        if (bookmarks.length === 0) {
          bookmarks = Array.from({ length: numPages }, (_, index) => ({
            id: uuid(),
            taxReturnIndex,
            pageIndex: index + 1,
            name: `Page ${index + 1}`,
          }));
        }

        return bookmarks;
      } catch (error) {
        console.error('Error processing bookmarks:', error);
        return [];
      }
    },

    handleItemClick(item) {
      // If item has children, toggle its open state
      if (item.children && item.children.length > 0) {
        const index = this.currentReturnData.openItems.indexOf(item.id);
        if (index > -1) {
          this.currentReturnData.openItems.splice(index, 1);
        } else {
          this.currentReturnData.openItems.push(item.id);
        }
      } else if (item.pageIndex !== null) {
        this.currentReturnIndex = item.taxReturnIndex;
        this.pdfPageIndex = item.pageIndex;
      }
    },

    initPollingForAllReturns() {
      this.allReturns.forEach((taxReturn, taxReturnIndex) => {
        if (
          // Main return
          taxReturn.original_file &&
          !taxReturn.tax_details_extraction_completed
        ) {
          const source = taxReturn.original_file;
          if (!this.pollingIntervals[source]) {
            this.pollingIntervals[source] = setInterval(() => {
              this.checkMainReturnExtraction(source);
            }, this.POLLING_INTERVAL);

            // Also check the status immediately
            this.checkMainReturnExtraction(source);
          }
        } else if (
          // Paper-filed return
          taxReturn.document_path &&
          (!taxReturn.tax_details_extraction_status ||
            (taxReturn.tax_details_extraction_status !== 'completed' &&
              taxReturn.tax_details_extraction_status !== 'cancelled'))
        ) {
          const source = taxReturn.document_path;

          if (!this.pollingIntervals[source]) {
            this.pollingIntervals[source] = setInterval(() => {
              this.checkPaperFiledReturnExtraction(taxReturnIndex, source);
            }, this.POLLING_INTERVAL);

            // Also check the status immediately
            this.checkPaperFiledReturnExtraction(taxReturnIndex, source);
          }
        }
      });
    },

    async checkMainReturnExtraction(source) {
      try {
        const response = await API.taxReturn.getTaxReturn({
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
        });

        const updatedTaxReturn = response.data;

        if (updatedTaxReturn.tax_details_extraction_completed) {
          // Stop polling
          if (this.pollingIntervals[source]) {
            clearInterval(this.pollingIntervals[source]);
            this.pollingIntervals[source] = null;
          }

          this.$set(this.allReturnsData, 0, {
            ...this.allReturnsData[0],
            taxDetails: updatedTaxReturn.tax_details,
          });

          this.$emit('tax-details-updated', updatedTaxReturn);
        }
      } catch (error) {
        console.error('Error polling for extraction status:', error);
      }
    },

    async checkPaperFiledReturnExtraction(taxReturnIndex, source) {
      try {
        const response = await API.taxReturn.getTaxReturnExtraction({
          companyId: this.currentCompany.id,
          source,
        });

        const extractionResult = response.data;

        if (extractionResult.status === 'completed') {
          // Stop polling
          if (this.pollingIntervals[source]) {
            clearInterval(this.pollingIntervals[source]);
            this.pollingIntervals[source] = null;
          }

          const paperFiledReturnData = this.allReturnsData[taxReturnIndex];

          if (
            paperFiledReturnData.taxDetailsExtractionStatus !== 'completed' &&
            paperFiledReturnData.taxDetailsExtractionStatus !== 'cancelled'
          ) {
            this.$set(this.allReturnsData, taxReturnIndex, {
              ...paperFiledReturnData,
              taxDetails: extractionResult.extracted_tax_details || [],
              taxDetailsExtractionStatus: 'completed',
            });
          }
        }
      } catch (error) {
        console.error(
          `Error polling for extraction status for ${source}:`,
          error,
        );
      }
    },

    clearAllPollingIntervals() {
      Object.values(this.pollingIntervals).forEach(interval => {
        if (interval) {
          clearInterval(interval);
        }
      });

      this.pollingIntervals = {};
    },

    async showSavingDialog() {
      if (this.savingDialogOpened) {
        return;
      }

      try {
        this.savingDialogOpened = true;

        const newTaxReturn = await this.save();

        if (newTaxReturn) {
          this.$emit('tax-details-updated', newTaxReturn);
        }
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      if (!this.editable) {
        return true;
      } else {
        await this.showSavingDialog();
        return true;
      }
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    allReturns() {
      const taxReturns = [this.taxReturn];

      if (this.taxReturn.paper_filed_returns) {
        taxReturns.push(...this.taxReturn.paper_filed_returns);
      }

      return taxReturns;
    },

    currentReturn() {
      return this.allReturns[this.currentReturnIndex];
    },

    currentReturnData() {
      return this.allReturnsData[this.currentReturnIndex];
    },

    editable() {
      return (
        this.taxReturn.status === 'action_required' &&
        this.taxReturn.tax_details_extraction_completed
      );
    },

    flattenedBookmarks() {
      const flatten = (items, parentId = null) => {
        return items.reduce((acc, item) => {
          const newItem = {
            ...item,
            id: item.id || uuid(),
            parentId: parentId,
          };

          acc.push(newItem);

          if (newItem.children && newItem.children.length > 0) {
            acc.push(...flatten(newItem.children, newItem.id));
          }

          return acc;
        }, []);
      };

      return flatten(this.currentReturnData.bookmarks);
    },

    activeItems() {
      const items = this.flattenedBookmarks
        .filter(item => item.pageIndex === this.pdfPageIndex)
        .map(item => item.id);

      return items;
    },
  },

  watch: {
    focusing(newFocusing) {
      if (newFocusing) {
        const focusingTaxReturn = this.allReturnsData[
          newFocusing.taxReturnIndex
        ];
        const detail = focusingTaxReturn.taxDetails[newFocusing.taxDetailIndex];

        if (detail && detail.rows) {
          const row = detail.rows[newFocusing.rowIndex];
          if (row && row.page && typeof row.page === 'number') {
            this.currentReturnIndex = newFocusing.taxReturnIndex;
            this.pdfPageIndex = row.page;

            if (row.bounding_box) {
              this.setBoundingBox(row.bounding_box);

              // Scroll to the bounding box after a short delay to ensure PDF is rendered
              this.$nextTick(() => {
                this.scrollToBoundingBox(row.bounding_box);
              });
            } else {
              this.clearBoundingBox();
            }
          }
        }
      } else {
        this.clearBoundingBox();
      }
    },
  },

  async mounted() {
    try {
      this.isLoadingAllReturns = true;

      await this.downloadAllReturnData();

      if (this.taxReturn.status === 'action_required') {
        await this.initPollingForAllReturns();
      }

      this.pdfPageIndex = 1;
    } finally {
      this.isLoadingAllReturns = false;
    }
  },

  async beforeDestroy() {
    // Clear polling interval if it exists
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    await this.beforeClose();
  },
};
</script>

<style scoped>
.full-page-height {
  height: 100dvh;
}

.left-sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  max-height: calc(100dvh - 120px);
  min-height: calc(100dvh - 120px);
  min-width: 260px;
  max-width: 260px;
  overflow: auto;
  padding: 20px 0px 32px;

  background-color: #fff;
  border-right: 1px solid var(--gray-300);
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.pdf-container {
  flex: auto;
  position: relative;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
}

.toolbar {
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 0 8px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-300);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  width: 100%;
  height: 100%;
  min-height: calc(100dvh - 176px);
  max-height: calc(100dvh - 176px);
}

.scrollable-container {
  overflow: auto;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}

.pdf-page-container {
  position: relative;
  min-height: 0;
  flex: auto;
  overflow: auto;
  flex-direction: column;
}

.pdf-wrapper {
  position: relative;
  display: inline-block;
  max-width: 1000px;
}

.right-sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  flex: auto;
  min-width: 320px;
  max-width: 320px;
  overflow: auto;
  padding: 32px 16px 90px; /* reserved bottom space for Hubspot chat */
  background-color: #fff;
  border-left: 1px solid var(--gray-300);
  z-index: 2;
}

.tax-summary-sidebar {
  width: 100%;
}

.sidebar-title {
  margin-bottom: 12px;
}

.tax-details-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tax-detail-card {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e0e0e0;
}

.card-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.title-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  height: 20px;
  width: 20px;
  margin: 0;
}

.editable-title-input {
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #ccc;
  padding: 2px 6px;
  border-radius: 4px;
  flex-grow: 1;
}

.detail-row-sidebar {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  font-size: 14px;
  min-height: 36px;
}

.row-label {
  flex: 1;
}

.row-value {
  text-align: right;
  min-width: 80px;
}

.amount-refunded {
  color: var(--green-700);
}

.amount-owed {
  color: var(--red-700);
}

.form-control {
  & >>> .v-input__control {
    min-height: 30px;
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd;
    border-radius: 8px;
    max-width: 120px;
    font-size: 14px;
  }

  & >>> .v-text-field .v-input__slot input {
    text-align: right;
  }

  & >>> .readonly-field .v-input__slot {
    background-color: #f1f1f1;
    box-shadow: none !important;
    color: #000;
  }

  & >>> .readonly-field .v-input__slot input {
    cursor: not-allowed;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500) !important;
  }
}

.sidebar-currency-input.errored {
  & >>> .v-input__slot {
    border: 2px solid var(--red-500) !important;
  }
}
.bookmark-label {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.2;
  cursor: pointer;
  font-size: 14px;
}

.bookmark-label:hover {
  color: var(--blue-600);
}

.bookmark-tree {
  & >>> .v-treeview-node__content {
    max-width: 100%;
  }
  & >>> .v-treeview-node__label {
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    margin-left: 0px;
    max-width: 100%;
    padding: 8px 4px;
  }
}

.focused-input {
  & >>> .v-input__slot {
    border: 2px solid var(--blue-500) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  }
}

.bounding-box-overlay {
  position: absolute;
  border: 2px solid var(--blue-500);
  background-color: rgba(59, 130, 246, 0.1);
  pointer-events: none;
  transition: all 0.2s ease-in-out;
  z-index: 10;
}

.tooltip-icon {
  cursor: help;
  padding-bottom: 2px;
}

.tooltip-icon:hover {
  color: var(--blue-600) !important;
}

.alert-extracting {
  border-radius: 8px;
}

.alert-extracting.errored {
  outline: 2px solid var(--red-500) !important;
}

.beta-chip {
  color: hsla(216, 91%, 52%, 1);
  background-color: rgba(21, 110, 244, 0.07);
  font-weight: bold;
  border-color: hsla(216, 91%, 52%, 0.07);
  border-width: 1px;
  border-style: solid;
}
</style>
